import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import { getPostBySlug, getAllPosts } from '../../data/blogPosts';
import { BRAND_LOGO_512_URL } from '../../config/brandAssets';
import './BlogPostPage.css';

const BlogPostPage = ({ darkMode, toggleDarkMode }) => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const allPosts = getAllPosts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <>
        <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <div className={`blog-post-page ${darkMode ? 'dark' : 'light'}`}>
          <div className="blog-post-not-found">
            <h1>Artículo no encontrado</h1>
            <p>El artículo que buscas no existe o ha sido movido.</p>
            <Link to="/blog" className="blog-back-link">← Volver al blog</Link>
          </div>
        </div>
        <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      </>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  // Obtener posts relacionados (misma categoría, excluyendo el actual)
  const relatedPosts = allPosts
    .filter(p => p.categoria === post.categoria && p.id !== post.id)
    .slice(0, 3);

  // Obtener post anterior y siguiente
  const currentIndex = allPosts.findIndex(p => p.id === post.id);
  const prevPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;

  // Renderizar contenido Markdown simple
  const renderContent = (content) => {
    // Convertir markdown básico a HTML
    let html = content
      // Links (before italic, so [texto](/ruta) no se rompe)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      // Lists
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      // Horizontal rule
      .replace(/^---$/gim, '<hr />')
      // Paragraphs
      .replace(/\n\n/gim, '</p><p>')
      // Tables (basic support)
      .replace(/\|(.+)\|/g, (match) => {
        const cells = match.split('|').filter(cell => cell.trim());
        if (cells.some(cell => cell.includes('---'))) return '';
        return '<tr>' + cells.map(cell => `<td>${cell.trim()}</td>`).join('') + '</tr>';
      });

    // Wrap lists
    html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
    // Clean up multiple ul tags
    html = html.replace(/<\/ul>\s*<ul>/g, '');

    return html;
  };

  // Schema.org Article JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.titulo,
    "description": post.extracto,
    "image": `https://e-asy.es${post.imagen}`,
    "author": {
      "@type": "Person",
      "name": post.autor,
      "url": "https://e-asy.es/sobre-nosotros"
    },
    "publisher": {
      "@type": "Organization",
      "name": "E-ASY",
      "logo": {
        "@type": "ImageObject",
        "url": BRAND_LOGO_512_URL
      }
    },
    "datePublished": post.fecha,
    "dateModified": post.fechaModificacion || post.fecha,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://e-asy.es/blog/${post.slug}`
    },
    "keywords": post.tags.join(', '),
    "articleSection": post.categoria,
    "wordCount": post.contenido.split(/\s+/).length,
    "timeRequired": `PT${post.tiempoLectura}M`
  };

  return (
    <>
      <SEO 
        title={`${post.titulo} | Blog E-ASY`}
        description={post.extracto}
        keywords={post.tags.join(', ')}
        canonical={`https://e-asy.es/blog/${post.slug}`}
        image={`https://e-asy.es${post.imagen}`}
        type="article"
        article={{
          publishedTime: post.fecha,
          modifiedTime: post.fechaModificacion || post.fecha,
          author: post.autor,
          section: post.categoria,
          tags: post.tags
        }}
      />
      
      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      <div className={`blog-post-page ${darkMode ? 'dark' : 'light'}`}>
        {/* Header */}
        <header className="blog-post-header">
          <div className="blog-post-header-content">
            <Link to="/blog" className="blog-back-link">← Volver al blog</Link>
            
            <div className="blog-post-meta-top">
              <span className="blog-post-category">{post.categoria}</span>
              <span className="blog-post-read-time">{post.tiempoLectura} min de lectura</span>
            </div>
            
            <h1 className="blog-post-title">{post.titulo}</h1>
            
            <p className="blog-post-excerpt">{post.extracto}</p>
            
            <div className="blog-post-author-info">
              <div className="blog-post-author-avatar">
                {post.autor.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="blog-post-author-details">
                <span className="blog-post-author-name">{post.autor}</span>
                <span className="blog-post-date">{formatDate(post.fecha)}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <article className="blog-post-content">
          <div className="blog-post-content-inner">
            <div 
              className="blog-post-body"
              dangerouslySetInnerHTML={{ __html: renderContent(post.contenido) }}
            />
            
            {/* Tags */}
            <div className="blog-post-tags">
              <span className="blog-post-tags-label">Etiquetas:</span>
              <div className="blog-post-tags-list">
                {post.tags.map(tag => (
                  <Link 
                    key={tag} 
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="blog-post-tag"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Share & CTA */}
        <section className="blog-post-cta">
          <div className="blog-post-cta-content">
            <h3 className="blog-post-cta-title">¿Te ha resultado útil este artículo?</h3>
            <p className="blog-post-cta-text">
              Si quieres implementar estas soluciones en tu empresa, en E-ASY podemos ayudarte. 
              Trabajamos con pymes de toda España desde nuestra sede en Tenerife.
            </p>
            <div className="blog-post-cta-buttons">
              <Link to="/contacto" className="blog-post-cta-btn primary">
                Solicitar consultoría gratuita
              </Link>
              <Link to="/servicios" className="blog-post-cta-btn secondary">
                Ver nuestros servicios
              </Link>
            </div>
          </div>
        </section>

        {/* Navigation */}
        <nav className="blog-post-nav">
          <div className="blog-post-nav-content">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="blog-post-nav-link prev">
                <span className="blog-post-nav-label">← Artículo anterior</span>
                <span className="blog-post-nav-title">{prevPost.titulo}</span>
              </Link>
            ) : <div />}
            
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="blog-post-nav-link next">
                <span className="blog-post-nav-label">Artículo siguiente →</span>
                <span className="blog-post-nav-title">{nextPost.titulo}</span>
              </Link>
            ) : <div />}
          </div>
        </nav>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="blog-post-related">
            <div className="blog-post-related-content">
              <h2 className="blog-post-related-title">Artículos relacionados</h2>
              <div className="blog-post-related-grid">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id} 
                    to={`/blog/${relatedPost.slug}`}
                    className="blog-post-related-card"
                  >
                    <span className="blog-post-related-category">{relatedPost.categoria}</span>
                    <h3 className="blog-post-related-card-title">{relatedPost.titulo}</h3>
                    <p className="blog-post-related-excerpt">{relatedPost.extracto}</p>
                    <span className="blog-post-related-link">Leer artículo →</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
      
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default BlogPostPage;



